import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useWorkbench } from '../WorkbenchContext';
import { formatCaseName } from './CaseReference';

// Use /api path for ALB routing in production
const API_BASE_URL = '/api';

function ReferenceLibrary() {
  const {
    activeCase,
    activeCaseId,
    linkReferenceToCase,
    unlinkReferenceFromCase,
    selectedReferenceCase,
    setSelectedReferenceCase
  } = useWorkbench();
  const [cases, setCases] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for scrolling to highlighted case
  const caseRefs = useRef({});

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourt, setSelectedCourt] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [selectedIssues, setSelectedIssues] = useState([]);

  // View state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [selectedCase, setSelectedCase] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    citation_full: '',
    citation_short: '',
    court: '',
    year: '',
    holding: ''
  });

  // Load cases and statistics on mount
  useEffect(() => {
    loadCases();
    loadStatistics();
  }, []);

  const loadCases = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (selectedCourt) params.append('court', selectedCourt);
      if (yearFrom) params.append('year_from', yearFrom);
      if (yearTo) params.append('year_to', yearTo);
      if (selectedIssues.length > 0) params.append('legal_issues', selectedIssues.join(','));

      const response = await axios.get(`${API_BASE_URL}/cases?${params.toString()}`, {
        withCredentials: true
      });

      setCases(response.data.cases);
    } catch (err) {
      console.error('Error loading cases:', err);
      setError(err.response?.data?.detail || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cases/statistics`, {
        withCredentials: true
      });
      setStatistics(response.data);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const handleSearch = () => {
    loadCases();
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedCourt('');
    setYearFrom('');
    setYearTo('');
    setSelectedIssues([]);
    setTimeout(loadCases, 0);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      alert('Please select a PDF file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('citation_full', uploadMetadata.citation_full);
      formData.append('citation_short', uploadMetadata.citation_short);
      formData.append('court', uploadMetadata.court);
      formData.append('year', uploadMetadata.year);
      formData.append('holding', uploadMetadata.holding);

      await axios.post(`${API_BASE_URL}/cases/upload-document`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Reset form
      setUploadFile(null);
      setUploadMetadata({
        citation_full: '',
        citation_short: '',
        court: '',
        year: '',
        holding: ''
      });
      setShowUploadModal(false);

      // Reload cases
      loadCases();
      loadStatistics();

      alert('Case uploaded successfully!');
    } catch (err) {
      console.error('Error uploading case:', err);
      alert(err.response?.data?.detail || 'Failed to upload case');
    }
  };

  const toggleIssue = (issue) => {
    const newIssues = selectedIssues.includes(issue)
      ? selectedIssues.filter(i => i !== issue)
      : [...selectedIssues, issue];
    setSelectedIssues(newIssues);
  };

  // Auto-search when filters change
  useEffect(() => {
    loadCases();
  }, [selectedIssues, selectedCourt]);

  // Handle scrolling to and highlighting selected reference case
  useEffect(() => {
    if (!selectedReferenceCase) return;

    const findAndShowCase = async () => {
      // First check if case is already in current list
      let targetCase = cases.find(c => c.case_id === selectedReferenceCase);

      if (targetCase) {
        // Case found in current list - scroll and show
        const caseElement = caseRefs.current[selectedReferenceCase];
        if (caseElement) {
          caseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setSelectedCase(targetCase);
        return;
      }

      // Case not in current list - search for it directly
      try {
        // Clear filters and search by case ID
        setSearchQuery(selectedReferenceCase);
        setSelectedCourt('');
        setYearFrom('');
        setYearTo('');
        setSelectedIssues([]);

        // Fetch cases with this search query
        const params = new URLSearchParams();
        params.append('query', selectedReferenceCase);

        const response = await axios.get(`${API_BASE_URL}/cases?${params.toString()}`, {
          withCredentials: true
        });

        const searchResults = response.data.cases;
        setCases(searchResults);

        // Find the case in search results
        targetCase = searchResults.find(c => c.case_id === selectedReferenceCase);

        if (targetCase) {
          // Small delay to let the DOM update before scrolling
          setTimeout(() => {
            const caseElement = caseRefs.current[selectedReferenceCase];
            if (caseElement) {
              caseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setSelectedCase(targetCase);
          }, 100);
        } else if (searchResults.length > 0) {
          // If exact match not found but we have results, show the first one
          setSelectedCase(searchResults[0]);
        }
      } catch (err) {
        console.error('Error searching for case:', err);
      }
    };

    // Only run once cases have initially loaded or if we need to search
    if (!loading) {
      findAndShowCase();
    }
  }, [selectedReferenceCase, loading]);

  // Clear selected reference case when component unmounts
  useEffect(() => {
    return () => {
      setSelectedReferenceCase(null);
    };
  }, []);

  return (
    <div className="h-full flex flex-col bg-galaxy-950">
      {/* Header */}
      <div className="bg-galaxy-900/50 border-b border-galaxy-800/50 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <span>&#128218;</span>
              Reference Library
            </h2>
            <p className="text-galaxy-300 text-sm mt-1">
              Precedent cases and legal authorities for citations
            </p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <span>+</span>
            Upload Reference
          </button>
        </div>

        {/* Active Case Context Banner */}
        {activeCase && (
          <div className="mt-4 bg-galaxy-700/30 border border-galaxy-600/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm">
              <span>&#9878;</span>
              <span className="text-galaxy-300">Linking to:</span>
              <span className="text-white font-medium">{activeCase.name}</span>
              <span className="text-galaxy-500">
                ({activeCase.linkedReferences.length} references linked)
              </span>
            </div>
          </div>
        )}

        {/* Statistics Summary */}
        {statistics && (
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="bg-galaxy-800/30 rounded-lg p-3">
              <div className="text-galaxy-400 text-xs uppercase">Total Cases</div>
              <div className="text-white text-2xl font-bold">{statistics.total_cases}</div>
            </div>
            <div className="bg-galaxy-800/30 rounded-lg p-3">
              <div className="text-galaxy-400 text-xs uppercase">Courts</div>
              <div className="text-white text-2xl font-bold">
                {Object.keys(statistics.by_court || {}).length}
              </div>
            </div>
            <div className="bg-galaxy-800/30 rounded-lg p-3">
              <div className="text-galaxy-400 text-xs uppercase">Legal Issues</div>
              <div className="text-white text-2xl font-bold">{statistics.unique_legal_issues}</div>
            </div>
            <div className="bg-galaxy-800/30 rounded-lg p-3">
              <div className="text-galaxy-400 text-xs uppercase">Time Span</div>
              <div className="text-white text-2xl font-bold">
                {Object.keys(statistics.by_decade || {}).length} decades
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Search and Filter Panel */}
      <div className="bg-galaxy-900/30 border-b border-galaxy-800/50 px-6 py-4">
        <div className="grid grid-cols-12 gap-4">
          {/* Text Search */}
          <div className="col-span-4">
            <label className="text-galaxy-300 text-sm mb-1 block">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Case name, holding, facts..."
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          {/* Court Filter */}
          <div className="col-span-3">
            <label className="text-galaxy-300 text-sm mb-1 block">Court</label>
            <select
              value={selectedCourt}
              onChange={(e) => setSelectedCourt(e.target.value)}
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white focus:outline-none focus:border-galaxy-500"
            >
              <option value="">All Courts</option>
              {statistics?.by_court && Object.keys(statistics.by_court).map(court => (
                <option key={court} value={court}>{court}</option>
              ))}
            </select>
          </div>

          {/* Year Range */}
          <div className="col-span-2">
            <label className="text-galaxy-300 text-sm mb-1 block">Year From</label>
            <input
              type="number"
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
              placeholder="1900"
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
            />
          </div>

          <div className="col-span-2">
            <label className="text-galaxy-300 text-sm mb-1 block">Year To</label>
            <input
              type="number"
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
              placeholder="2025"
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="col-span-1 flex gap-2 items-end">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg transition-colors"
            >
              Search
            </button>
          </div>
        </div>

        {/* Legal Issues Filter */}
        {statistics?.legal_issues && statistics.legal_issues.length > 0 && (
          <div className="mt-4">
            <label className="text-galaxy-300 text-sm mb-2 block">Legal Issues</label>
            <div className="flex flex-wrap gap-2">
              {statistics.legal_issues.slice(0, 15).map(issue => (
                <button
                  key={issue}
                  onClick={() => toggleIssue(issue)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedIssues.includes(issue)
                      ? 'bg-galaxy-600 text-white'
                      : 'bg-galaxy-800/50 text-galaxy-300 hover:bg-galaxy-700/50'
                  }`}
                >
                  {issue}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active Filters */}
        {(searchQuery || selectedCourt || yearFrom || yearTo || selectedIssues.length > 0) && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-galaxy-400 text-sm">Active filters:</span>
            <button
              onClick={handleReset}
              className="px-3 py-1 bg-galaxy-700/50 hover:bg-galaxy-600/50 text-galaxy-300 rounded text-sm transition-colors"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Cases Display */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-galaxy-400">Loading cases...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{error}</div>
          </div>
        ) : cases.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-galaxy-400">No cases found matching your criteria</div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cases.map(caseItem => (
              <CaseCard
                key={caseItem.case_id}
                ref={el => caseRefs.current[caseItem.case_id] = el}
                caseItem={caseItem}
                onClick={() => setSelectedCase(caseItem)}
                activeCase={activeCase}
                activeCaseId={activeCaseId}
                onLinkToCase={() => linkReferenceToCase(activeCaseId, caseItem.case_id)}
                onUnlinkFromCase={() => unlinkReferenceFromCase(activeCaseId, caseItem.case_id)}
                isHighlighted={selectedReferenceCase === caseItem.case_id}
              />
            ))}
          </div>
        ) : (
          <CaseTable cases={cases} onSelectCase={setSelectedCase} />
        )}
      </div>

      {/* Case Detail Modal */}
      {selectedCase && (
        <CaseDetailModal
          caseItem={selectedCase}
          onClose={() => {
            setSelectedCase(null);
            setSelectedReferenceCase(null);
          }}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadCaseModal
          uploadFile={uploadFile}
          setUploadFile={setUploadFile}
          uploadMetadata={uploadMetadata}
          setUploadMetadata={setUploadMetadata}
          onSubmit={handleUpload}
          onClose={() => setShowUploadModal(false)}
        />
      )}
    </div>
  );
}

const CaseCard = React.forwardRef(function CaseCard(
  { caseItem, onClick, activeCase, activeCaseId, onLinkToCase, onUnlinkFromCase, isHighlighted },
  ref
) {
  const citation = caseItem.citation || {};
  const year = citation.year || 'Unknown';
  const court = citation.court || 'Unknown Court';
  const isLinked = activeCase?.linkedReferences?.includes(caseItem.case_id);

  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (isLinked) {
      onUnlinkFromCase();
    } else {
      onLinkToCase();
    }
  };

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`bg-galaxy-800/30 border rounded-lg p-4 hover:bg-galaxy-800/50 transition-all cursor-pointer ${
        isHighlighted
          ? 'border-indigo-500 bg-indigo-900/20 ring-2 ring-indigo-500/50'
          : isLinked
          ? 'border-galaxy-500 bg-galaxy-800/40'
          : 'border-galaxy-700/50 hover:border-galaxy-600/50'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-semibold text-sm">
              {citation.short || 'Untitled Case'}
            </h3>
            {isHighlighted && (
              <span className="px-1.5 py-0.5 bg-indigo-600/50 text-indigo-200 text-xs rounded animate-pulse">
                Cited
              </span>
            )}
            {isLinked && !isHighlighted && (
              <span className="px-1.5 py-0.5 bg-galaxy-600/50 text-galaxy-200 text-xs rounded">
                Linked
              </span>
            )}
          </div>
          <p className="text-galaxy-400 text-xs mt-1">{citation.reporter || citation.full}</p>
        </div>
        <span className="text-galaxy-500 text-xs bg-galaxy-900/50 px-2 py-1 rounded">
          {year}
        </span>
      </div>

      <p className="text-galaxy-300 text-xs mb-3 line-clamp-2">
        {caseItem.holding || 'No holding available'}
      </p>

      <div className="flex items-center justify-between">
        <span className="text-galaxy-500 text-xs">{court}</span>
        <div className="flex items-center gap-2">
          {caseItem.legal_issues && caseItem.legal_issues.length > 0 && (
            <span className="text-galaxy-500 text-xs">
              {caseItem.legal_issues.length} issue{caseItem.legal_issues.length !== 1 ? 's' : ''}
            </span>
          )}
          {activeCaseId && (
            <button
              onClick={handleLinkClick}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                isLinked
                  ? 'bg-galaxy-600/50 text-galaxy-200 hover:bg-red-600/50 hover:text-red-200'
                  : 'bg-galaxy-700/50 text-galaxy-300 hover:bg-galaxy-600/50 hover:text-white'
              }`}
              title={isLinked ? 'Unlink from active case' : 'Link to active case'}
            >
              {isLinked ? 'Unlink' : 'Link to Case'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

function CaseTable({ cases, onSelectCase }) {
  return (
    <div className="bg-galaxy-800/30 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-galaxy-900/50">
          <tr>
            <th className="text-left px-4 py-3 text-galaxy-300 text-sm font-semibold">Case Name</th>
            <th className="text-left px-4 py-3 text-galaxy-300 text-sm font-semibold">Citation</th>
            <th className="text-left px-4 py-3 text-galaxy-300 text-sm font-semibold">Court</th>
            <th className="text-left px-4 py-3 text-galaxy-300 text-sm font-semibold">Year</th>
            <th className="text-left px-4 py-3 text-galaxy-300 text-sm font-semibold">Issues</th>
          </tr>
        </thead>
        <tbody>
          {cases.map(caseItem => {
            const citation = caseItem.citation || {};
            return (
              <tr
                key={caseItem.case_id}
                onClick={() => onSelectCase(caseItem)}
                className="border-t border-galaxy-700/50 hover:bg-galaxy-800/50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-white text-sm">{citation.short || 'Untitled'}</td>
                <td className="px-4 py-3 text-galaxy-300 text-sm">{citation.reporter || citation.full}</td>
                <td className="px-4 py-3 text-galaxy-300 text-sm">{citation.court || 'Unknown'}</td>
                <td className="px-4 py-3 text-galaxy-300 text-sm">{citation.year || 'N/A'}</td>
                <td className="px-4 py-3 text-galaxy-300 text-sm">
                  {caseItem.legal_issues ? caseItem.legal_issues.length : 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CaseDetailModal({ caseItem, onClose }) {
  const citation = caseItem.citation || {};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-galaxy-900 border border-galaxy-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-galaxy-900 border-b border-galaxy-700 px-6 py-4 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-white">{citation.short || 'Untitled Case'}</h2>
            <p className="text-galaxy-300 text-sm mt-1">{citation.full}</p>
          </div>
          <button
            onClick={onClose}
            className="text-galaxy-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Citation Info */}
          <div>
            <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Citation</h3>
            <div className="bg-galaxy-800/30 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-galaxy-400">Court:</span>
                <span className="text-white">{citation.court || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-galaxy-400">Year:</span>
                <span className="text-white">{citation.year || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-galaxy-400">Reporter:</span>
                <span className="text-white font-mono text-sm">{citation.reporter || 'N/A'}</span>
              </div>
              {caseItem.bluebook_citation && (
                <div className="mt-3 pt-3 border-t border-galaxy-700/50">
                  <span className="text-galaxy-400 block mb-1">Bluebook Citation:</span>
                  <code className="text-white font-mono text-sm bg-galaxy-900/50 px-2 py-1 rounded">
                    {caseItem.bluebook_citation}
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* Parties */}
          {caseItem.parties && caseItem.parties.length > 0 && (
            <div>
              <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Parties</h3>
              <div className="bg-galaxy-800/30 rounded-lg p-4">
                <p className="text-white">{caseItem.parties.join(', ')}</p>
              </div>
            </div>
          )}

          {/* Holding */}
          {caseItem.holding && (
            <div>
              <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Holding</h3>
              <div className="bg-galaxy-800/30 rounded-lg p-4">
                <p className="text-white leading-relaxed">{caseItem.holding}</p>
              </div>
            </div>
          )}

          {/* Key Facts */}
          {caseItem.key_facts && (
            <div>
              <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Key Facts</h3>
              <div className="bg-galaxy-800/30 rounded-lg p-4">
                <p className="text-white leading-relaxed">{caseItem.key_facts}</p>
              </div>
            </div>
          )}

          {/* Legal Issues */}
          {caseItem.legal_issues && caseItem.legal_issues.length > 0 && (
            <div>
              <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Legal Issues</h3>
              <div className="flex flex-wrap gap-2">
                {caseItem.legal_issues.map((issue, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-galaxy-600/50 text-white rounded-full text-sm"
                  >
                    {issue}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Key Quotes */}
          {caseItem.key_quotes && caseItem.key_quotes.length > 0 && (
            <div>
              <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Key Quotes</h3>
              <div className="space-y-3">
                {caseItem.key_quotes.map((quote, idx) => (
                  <div key={idx} className="bg-galaxy-800/30 rounded-lg p-4 border-l-4 border-galaxy-600">
                    <p className="text-white italic">"{quote}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* External Link to Case */}
          <div>
            <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">External Resources</h3>
            <div className="bg-galaxy-800/30 rounded-lg p-4">
              <a
                href={`https://scholar.google.com/scholar?q=${encodeURIComponent(citation.full || citation.short)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-galaxy-400 hover:text-galaxy-300 underline flex items-center gap-2"
              >
                <span>🔍</span>
                Search on Google Scholar
              </a>
            </div>
          </div>

          {/* Case ID */}
          <div>
            <h3 className="text-galaxy-400 text-sm font-semibold uppercase mb-2">Reference ID</h3>
            <div className="bg-galaxy-800/30 rounded-lg p-4">
              <code className="text-galaxy-300 font-mono text-sm">
                [CASE:{caseItem.case_id}]
              </code>
              <p className="text-galaxy-500 text-xs mt-2">
                Use this ID format when citing this case in documents
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-galaxy-900 border-t border-galaxy-700 px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-galaxy-700 hover:bg-galaxy-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadCaseModal({ uploadFile, setUploadFile, uploadMetadata, setUploadMetadata, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-galaxy-900 border border-galaxy-700 rounded-lg max-w-2xl w-full">
        <div className="border-b border-galaxy-700 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Upload New Case</h2>
          <p className="text-galaxy-400 text-sm mt-1">Add a new legal case to the library</p>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-4 space-y-4">
          {/* File Upload */}
          <div>
            <label className="text-galaxy-300 text-sm mb-2 block">PDF Document *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setUploadFile(e.target.files[0])}
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-galaxy-600 file:text-white hover:file:bg-galaxy-500"
              required
            />
          </div>

          {/* Full Citation */}
          <div>
            <label className="text-galaxy-300 text-sm mb-2 block">Full Citation *</label>
            <input
              type="text"
              value={uploadMetadata.citation_full}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, citation_full: e.target.value })}
              placeholder="e.g., Brown v. Board of Education, 347 U.S. 483 (1954)"
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
              required
            />
          </div>

          {/* Short Name */}
          <div>
            <label className="text-galaxy-300 text-sm mb-2 block">Short Case Name *</label>
            <input
              type="text"
              value={uploadMetadata.citation_short}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, citation_short: e.target.value })}
              placeholder="e.g., Brown v. Board of Education"
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Court */}
            <div>
              <label className="text-galaxy-300 text-sm mb-2 block">Court *</label>
              <input
                type="text"
                value={uploadMetadata.court}
                onChange={(e) => setUploadMetadata({ ...uploadMetadata, court: e.target.value })}
                placeholder="e.g., U.S. Supreme Court"
                className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
                required
              />
            </div>

            {/* Year */}
            <div>
              <label className="text-galaxy-300 text-sm mb-2 block">Year *</label>
              <input
                type="number"
                value={uploadMetadata.year}
                onChange={(e) => setUploadMetadata({ ...uploadMetadata, year: e.target.value })}
                placeholder="e.g., 1954"
                className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
                required
              />
            </div>
          </div>

          {/* Holding */}
          <div>
            <label className="text-galaxy-300 text-sm mb-2 block">Holding</label>
            <textarea
              value={uploadMetadata.holding}
              onChange={(e) => setUploadMetadata({ ...uploadMetadata, holding: e.target.value })}
              placeholder="Brief description of the legal principle established..."
              rows={3}
              className="w-full px-3 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg transition-colors"
            >
              Upload Case
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-galaxy-700 hover:bg-galaxy-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReferenceLibrary;
